<template>
  <div class="page">
    <div class="bg-grid" />
    <TopBar />
    <ProjectNav :project="project" />

    <main class="content">
      <!-- Stat cards -->
      <div class="stat-row">
        <StatCard
          label="TOTAL EXAMPLES"
          :value="totalExamples.toLocaleString()"
          sub="sft + dpo eligible"
        />
        <StatCard
          label="SFT PAIRS"
          :value="data_quality.implicit_positive + data_quality.verified_positive > 0
            ? (data_quality.implicit_positive + data_quality.verified_positive).toLocaleString()
            : latestExport ? latestExport.sft_count.toLocaleString() : '0'"
          sub="supervised fine-tuning"
          trend="positive"
        />
        <StatCard
          label="DPO PAIRS"
          :value="data_quality.dpo_pairs.toLocaleString()"
          sub="preference optimization"
          :trend="data_quality.dpo_pairs > 0 ? 'positive' : 'neutral'"
        />
      </div>

      <div class="main-grid">
        <!-- Export history -->
        <div class="history-col">
          <SectionHeader label="Export History" :meta="`${exports.length} exports`" />

          <div v-if="exports.length === 0" class="empty-state">
            <p class="empty-title">No exports yet</p>
            <p class="empty-body">Generate your first fine-tune export to get started.</p>
          </div>

          <div v-else class="export-list">
            <div v-for="exp in exports" :key="exp.id" class="export-row">
              <div class="export-left">
                <span
                  class="export-dot"
                  :class="{
                    green:  exp.status === 'ready',
                    yellow: exp.status === 'generating' || exp.status === 'pending',
                    red:    exp.status === 'failed',
                  }"
                />
                <div class="export-meta">
                  <span class="export-date">{{ formatDate(exp.created_at) }}</span>
                  <span v-if="exp.status === 'ready'" class="export-counts">
                    {{ exp.sft_count.toLocaleString() }} SFT · {{ exp.dpo_count.toLocaleString() }} DPO
                  </span>
                  <span v-else-if="exp.status === 'failed'" class="export-error">
                    {{ exp.error_message || 'Export failed' }}
                  </span>
                  <span v-else class="export-pending">{{ exp.status }}…</span>
                </div>
              </div>
              <div v-if="exp.status === 'ready'" class="export-downloads">
                <a :href="`/projects/${project.uuid}/finetune/exports/${exp.id}/download_sft`" class="dl-btn">
                  ↓ SFT
                </a>
                <a :href="`/projects/${project.uuid}/finetune/exports/${exp.id}/download_dpo`" class="dl-btn">
                  ↓ DPO
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Right column -->
        <div class="right-col">
          <!-- Data quality -->
          <div class="quality-panel">
            <div class="panel-head">
              <span class="panel-label">DATA QUALITY</span>
              <span class="panel-meta">training signal breakdown</span>
            </div>
            <div class="quality-body">
              <div class="quality-row">
                <span class="quality-label">Implicit positive</span>
                <ProgressBar
                  :value="data_quality.implicit_positive"
                  :max="qualityMax"
                  color="amber"
                />
                <span class="quality-count mono">{{ data_quality.implicit_positive.toLocaleString() }}</span>
              </div>
              <div class="quality-row">
                <span class="quality-label">Verified positive</span>
                <ProgressBar
                  :value="data_quality.verified_positive"
                  :max="qualityMax"
                  color="green"
                />
                <span class="quality-count mono">{{ data_quality.verified_positive.toLocaleString() }}</span>
              </div>
              <div class="quality-row">
                <span class="quality-label">DPO pairs</span>
                <ProgressBar
                  :value="data_quality.dpo_pairs"
                  :max="qualityMax"
                  color="blue"
                />
                <span class="quality-count mono">{{ data_quality.dpo_pairs.toLocaleString() }}</span>
              </div>
              <div class="quality-legend">
                <span class="legend-item amber-text">amber = implicit</span>
                <span class="legend-item green-text">green = verified</span>
                <span class="legend-item blue-text">blue = DPO</span>
              </div>
            </div>
          </div>

          <!-- Format info -->
          <div class="format-panel">
            <div class="panel-head">
              <span class="panel-label">OUTPUT FORMAT</span>
            </div>
            <div class="format-body">
              <div class="format-row">
                <span class="format-name">SFT JSONL</span>
                <span class="format-desc">OpenAI messages format · system + user → assistant · compatible with OpenAI, HuggingFace TRL, Axolotl, LLaMA-Factory, local models</span>
              </div>
              <div class="format-row">
                <span class="format-name">DPO JSONL</span>
                <span class="format-desc">Standard DPO triplet format · prompt + chosen + rejected · apply on top of SFT-tuned model for preference alignment</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Generate panel -->
      <div class="generate-panel">
        <div class="generate-info">
          <span class="generate-title">GENERATE FINE-TUNE EXPORT</span>
          <span class="generate-sub">
            Collects all eligible training examples from this project's message history
            and feedback into two JSONL files ready for fine-tuning any model.
            Generation runs in the background — download links appear when ready.
          </span>
        </div>
        <button class="generate-btn" :disabled="generating" @click="triggerExport">
          {{ generating ? "QUEUING…" : "GENERATE EXPORT" }}
        </button>
      </div>
    </main>
  </div>
</template>

<script setup>
import { computed, ref } from "vue"
import { router } from "@inertiajs/vue3"
import TopBar from "../../components/TopBar.vue"
import ProjectNav from "../../components/ProjectNav.vue"
import StatCard from "../../components/StatCard.vue"
import SectionHeader from "../../components/SectionHeader.vue"
import ProgressBar from "../../components/ProgressBar.vue"

const props = defineProps({
  project:      { type: Object, required: true },
  exports:      { type: Array,  default: () => [] },
  data_quality: { type: Object, default: () => ({ implicit_positive: 0, verified_positive: 0, dpo_pairs: 0 }) },
})

const generating = ref(false)

const latestExport = computed(() => props.exports.find((e) => e.status === "ready") ?? null)

const totalExamples = computed(() =>
  props.data_quality.implicit_positive +
  props.data_quality.verified_positive +
  props.data_quality.dpo_pairs
)

const qualityMax = computed(() =>
  Math.max(
    props.data_quality.implicit_positive,
    props.data_quality.verified_positive,
    props.data_quality.dpo_pairs,
    1
  )
)

async function triggerExport() {
  generating.value = true
  try {
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const res   = await fetch(`/projects/${props.project.uuid}/finetune/exports`, {
      method:  "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
    })
    if (res.ok) {
      router.reload()
    }
  } finally {
    generating.value = false
  }
}

function formatDate(dateStr) {
  if (!dateStr) return "—"
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" })
}
</script>

<style scoped>
.page {
  min-height: 100vh;
  background-color: #0a0a0b;
  position: relative;
}

.bg-grid {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background-image:
    linear-gradient(rgba(245, 166, 35, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(245, 166, 35, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
}

.content {
  position: relative;
  z-index: 1;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.stat-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.main-grid {
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 16px;
  align-items: start;
}

.history-col,
.right-col {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Empty state */
.empty-state {
  padding: 40px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.025);
}

.empty-title {
  font-family: var(--font-display);
  font-size: 15px;
  font-weight: 700;
  color: #e8e6e3;
  margin: 0 0 6px;
}

.empty-body {
  font-family: var(--font-mono);
  font-size: 11px;
  color: rgba(232, 230, 227, 0.45);
  margin: 0;
}

/* Export list */
.export-list {
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  overflow: hidden;
}

.export-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  gap: 16px;
  transition: background 0.15s;
}

.export-row:first-child { border-top: none; }
.export-row:hover { background: rgba(245, 166, 35, 0.04); }

.export-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.export-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.export-dot.green  { background: #4ade80; box-shadow: 0 0 6px rgba(74, 222, 128, 0.5); }
.export-dot.yellow { background: #fbbf24; box-shadow: 0 0 6px rgba(251, 191, 36, 0.5); }
.export-dot.red    { background: #f87171; box-shadow: 0 0 6px rgba(248, 113, 113, 0.5); }

.export-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.export-date {
  font-family: var(--font-mono);
  font-size: 12px;
  color: #e8e6e3;
}

.export-counts {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.45);
}

.export-error {
  font-family: var(--font-mono);
  font-size: 10px;
  color: #f87171;
}

.export-pending {
  font-family: var(--font-mono);
  font-size: 10px;
  color: #fbbf24;
}

.export-downloads {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.dl-btn {
  padding: 4px 10px;
  background: rgba(245, 166, 35, 0.1);
  border: 1px solid rgba(245, 166, 35, 0.2);
  border-radius: 3px;
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: #f5a623;
  text-decoration: none;
  transition: all 0.15s;
}

.dl-btn:hover {
  background: rgba(245, 166, 35, 0.2);
  border-color: rgba(245, 166, 35, 0.4);
}

/* Right column panels */
.quality-panel,
.format-panel {
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  overflow: hidden;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.panel-label {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: rgba(232, 230, 227, 0.35);
}

.panel-meta {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.25);
}

.quality-body {
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.quality-row {
  display: grid;
  grid-template-columns: 110px 1fr 36px;
  align-items: center;
  gap: 8px;
}

.quality-label {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.5);
}

.quality-count {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.35);
  text-align: right;
}

.quality-legend {
  display: flex;
  gap: 10px;
  padding-top: 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  flex-wrap: wrap;
}

.legend-item {
  font-family: var(--font-mono);
  font-size: 9px;
}

.amber-text { color: #f5a623; }
.green-text  { color: #4ade80; }
.blue-text   { color: #60a5fa; }

.format-body {
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.format-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.format-row:last-child {
  padding-bottom: 0;
  border-bottom: none;
}

.format-name {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: #f5a623;
}

.format-desc {
  font-family: var(--font-mono);
  font-size: 9px;
  color: rgba(232, 230, 227, 0.35);
  line-height: 1.6;
}

/* Generate panel */
.generate-panel {
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.generate-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.generate-title {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: rgba(232, 230, 227, 0.35);
}

.generate-sub {
  font-family: var(--font-mono);
  font-size: 11px;
  color: rgba(232, 230, 227, 0.35);
  line-height: 1.6;
  max-width: 640px;
}

.generate-btn {
  padding: 10px 24px;
  background: #f5a623;
  border: none;
  border-radius: 4px;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.2px;
  color: #0a0a0b;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: opacity 0.2s;
}

.generate-btn:hover:not(:disabled) { opacity: 0.85; }
.generate-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.mono {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.35);
}
</style>
